/**
 * Преобразование ошибок API в человекочитаемые сообщения
 */

interface ApiError {
  message?: string;
  error?: string;
  statusCode?: number;
}

const ERROR_MESSAGES: Record<string, string> = {
  // Auth
  'Invalid code': 'Неверный код подтверждения',
  'Code expired': 'Код истёк, запросите новый',
  'User not found': 'Пользователь не найден',
  'Invalid credentials': 'Неверный логин или пароль',
  'Invalid invite token': 'Недействительный токен приглашения',
  'Invite already accepted': 'Приглашение уже было принято',
  'Invite expired': 'Приглашение истекло, запросите новое',
  'Login already taken': 'Этот логин уже занят',

  // Mailbox
  'mailboxId is required': 'Не выбран почтовый ящик',
  'Mailbox not found': 'Почтовый ящик не найден',
  'Email already exists': 'Этот email уже добавлен',
  'Failed to connect to IMAP': 'Не удалось подключиться к почтовому серверу',
  'Invalid IMAP credentials': 'Неверные данные для IMAP',
  'Connection failed': 'Не удалось подключиться к серверу',

  // Thread
  'Thread not found': 'Переписка не найдена',

  // Network
  'Network Error': 'Ошибка сети. Проверьте подключение к интернету',
  'timeout': 'Превышено время ожидания',
};

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Неверный запрос',
  401: 'Требуется авторизация',
  403: 'Доступ запрещён',
  404: 'Не найдено',
  409: 'Конфликт данных',
  422: 'Некорректные данные',
  429: 'Слишком много запросов, попробуйте позже',
  500: 'Ошибка сервера',
  502: 'Сервер недоступен',
  503: 'Сервис временно недоступен',
};

/**
 * Диагностические подсказки для технических ошибок IMAP/SMTP.
 * Паттерн → человечное объяснение + что делать.
 */
const CONNECTION_DIAGNOSTICS: Array<{ pattern: RegExp; message: string }> = [
  { pattern: /ECONNREFUSED/i, message: 'Сервер отклонил подключение. Проверьте хост и порт' },
  { pattern: /ENOTFOUND/i, message: 'Сервер не найден. Проверьте адрес хоста (опечатка?)' },
  { pattern: /ETIMEDOUT|timed?\s*out/i, message: 'Сервер не ответил. Проверьте порт и настройки шифрования' },
  { pattern: /ECONNRESET/i, message: 'Соединение разорвано. Возможно, неверный порт или тип шифрования' },
  { pattern: /self.signed|DEPTH_ZERO_SELF_SIGNED/i, message: 'Сертификат сервера самоподписанный. Обратитесь к администратору' },
  { pattern: /certificate|CERT_/i, message: 'Ошибка SSL-сертификата. Проверьте настройки шифрования' },
  { pattern: /auth|login|password|credentials/i, message: 'Ошибка авторизации. Проверьте логин и пароль' },
  { pattern: /STARTTLS/i, message: 'Сервер требует STARTTLS. Выберите режим шифрования STARTTLS' },
  { pattern: /too many connections/i, message: 'Превышен лимит подключений. Попробуйте позже' },
  { pattern: /getaddrinfo/i, message: 'Не удалось определить адрес сервера. Проверьте правильность хоста' },
];

export function diagnoseConnectionError(message: string): string | null {
  for (const { pattern, message: diagnostic } of CONNECTION_DIAGNOSTICS) {
    if (pattern.test(message)) return diagnostic;
  }
  return null;
}

export function getErrorMessage(error: unknown): string {
  // Axios error
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: {
        data?: ApiError;
        status?: number;
      };
      message?: string;
    };

    const data = axiosError.response?.data;
    const status = axiosError.response?.status;

    // Сообщение из API
    if (data?.message) {
      const translatedMessage = ERROR_MESSAGES[data.message];
      if (translatedMessage) return translatedMessage;
      const diagnostic = diagnoseConnectionError(data.message);
      if (diagnostic) return diagnostic;
      return data.message;
    }

    if (data?.error) {
      const translatedError = ERROR_MESSAGES[data.error];
      if (translatedError) return translatedError;
      const diagnostic = diagnoseConnectionError(data.error);
      if (diagnostic) return diagnostic;
      return data.error;
    }

    // details поле (mailbox creation возвращает { error, details })
    const details = (data as Record<string, unknown>)?.details;
    if (typeof details === 'string') {
      const diagnostic = diagnoseConnectionError(details);
      if (diagnostic) return diagnostic;
    }

    // Статус код
    if (status && STATUS_MESSAGES[status]) {
      return STATUS_MESSAGES[status];
    }

    // Сообщение ошибки axios
    if (axiosError.message) {
      const translatedMessage = ERROR_MESSAGES[axiosError.message];
      if (translatedMessage) return translatedMessage;
    }
  }

  // Обычная Error
  if (error instanceof Error) {
    const translatedMessage = ERROR_MESSAGES[error.message];
    if (translatedMessage) return translatedMessage;
    return error.message;
  }

  // Строка
  if (typeof error === 'string') {
    const translatedMessage = ERROR_MESSAGES[error];
    if (translatedMessage) return translatedMessage;
    return error;
  }

  return 'Произошла ошибка';
}
